import React, { useRef, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { acquireModalScrollLock, ROUTE_DISMISS_MODALS_EVENT } from '../../utils/modalUtils';
import { usePresenceTransition } from '../../hooks/usePresenceTransition';

/**
 * ADS Dialog — canonical modal shell (Phase 3).
 * Portaled to document.body + scroll-locked so placement is viewport-centered
 * independent of page scroll / nested overflow ancestors.
 * Enter/exit use --ads-duration-modal-open/close (presence keeps mount during exit).
 */
const AdsDialog = ({
  open,
  onClose,
  title,
  children,
  footer,
  fullscreen = false,
  compact = false,
  nested = false,
  /** 'confirm' → above all modal layers (unsaved-close / AdsConfirmDialog) */
  layer = null,
  className = '',
  backdropClassName = '',
  headerClassName = '',
  bodyClassName = '',
  headerType,
  showClose = true,
  closeOnBackdrop = true,
  closeLabel,
  icon,
  dialogStyle,
}) => {
  const { isRTL, t } = useLanguage();
  const mouseDownOnBackdropRef = useRef(false);
  const dialogRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const titleId = useId();
  const resolvedCloseLabel = closeLabel || t('ecartModal.closeAria');
  const { present, closing } = usePresenceTransition(Boolean(open), { exitMs: 160 });

  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open || closing) return;
    dialogRef.current?.focus();
  }, [open, closing]);

  useEffect(() => {
    if (!present) return undefined;
    return acquireModalScrollLock();
  }, [present]);

  // Keep-alive hosts hide with display:none but portals escape to body —
  // close on shell route change so dialogs don't overlay the next page.
  useEffect(() => {
    const onRouteDismiss = () => {
      if (open) onCloseRef.current?.();
    };
    window.addEventListener(ROUTE_DISMISS_MODALS_EVENT, onRouteDismiss);
    return () => window.removeEventListener(ROUTE_DISMISS_MODALS_EVENT, onRouteDismiss);
  }, [open]);

  useEffect(() => {
    if (!open || closing) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onCloseRef.current?.(event);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, closing]);

  if (!present || typeof document === 'undefined') return null;

  const handleBackdropMouseDown = (event) => {
    mouseDownOnBackdropRef.current = event.target === event.currentTarget;
  };

  const handleBackdropMouseUp = (event) => {
    const startedOnBackdrop = mouseDownOnBackdropRef.current;
    const endedOnBackdrop = event.target === event.currentTarget;
    mouseDownOnBackdropRef.current = false;
    if (!closing && closeOnBackdrop && startedOnBackdrop && endedOnBackdrop) {
      onClose?.(event);
    }
  };

  const handleCloseClick = (event) => {
    event.stopPropagation();
    if (!closing) onClose?.(event);
  };

  const nestLevel = nested === 2 || nested === '2' ? 2 : nested ? 1 : 0;
  const isConfirmLayer = layer === 'confirm';

  const backdropClass = [
    'ads-dialog-backdrop',
    'modal-backdrop',
    fullscreen ? 'ads-dialog-backdrop--fullscreen mb-backdrop--fullscreen' : '',
    nestLevel === 1 ? 'ads-dialog-backdrop--nested' : '',
    nestLevel === 2 ? 'ads-dialog-backdrop--nested-2' : '',
    isConfirmLayer ? 'ads-dialog-backdrop--confirm' : '',
    closing ? 'ads-dialog-backdrop--closing' : 'ads-dialog-backdrop--open',
    backdropClassName,
  ]
    .filter(Boolean)
    .join(' ');

  const dialogClass = [
    'ads-dialog',
    'modal-content',
    fullscreen ? 'ads-dialog--fullscreen mb-content--fullscreen' : '',
    compact ? 'ads-dialog--compact mb-content--compact' : '',
    nestLevel === 1 ? 'ads-dialog--nested' : '',
    nestLevel === 2 ? 'ads-dialog--nested-2' : '',
    isConfirmLayer ? 'ads-dialog--confirm' : '',
    closing ? 'ads-dialog--closing' : 'ads-dialog--open',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const headerClass = [
    'ads-dialog__header',
    'modal-header',
    'mb-header',
    fullscreen ? 'mb-header--fullscreen' : '',
    headerType ? `ads-dialog__header--${headerType}` : '',
    headerClassName,
  ]
    .filter(Boolean)
    .join(' ');

  const bodyClass = [
    'ads-dialog__body',
    'modal-body',
    'mb-body',
    compact ? 'ads-dialog__body--compact mb-body--compact' : '',
    fullscreen ? 'ads-dialog__body--fullscreen mb-body--fullscreen' : '',
    bodyClassName,
  ]
    .filter(Boolean)
    .join(' ');

  return createPortal(
    <div
      className={backdropClass}
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
      role="presentation"
      aria-hidden={closing ? true : undefined}
    >
      <div
        ref={dialogRef}
        className={dialogClass}
        style={dialogStyle}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        dir={isRTL ? 'rtl' : 'ltr'}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
      >
        {(title || showClose || icon) && (
          <div className={headerClass}>
            {icon}
            {title ? (
              <h2 id={titleId} className="ads-dialog__title mb-title">
                {title}
              </h2>
            ) : null}
            {showClose ? (
              <button
                type="button"
                className="ads-dialog__close modal-close mb-close"
                onClick={handleCloseClick}
                aria-label={resolvedCloseLabel}
              >
                <X size={18} strokeWidth={1.5} aria-hidden />
              </button>
            ) : null}
          </div>
        )}
        <div className={bodyClass}>{children}</div>
        {footer ? <div className="ads-dialog__footer modal-footer">{footer}</div> : null}
      </div>
    </div>,
    document.body
  );
};

export const AdsDialogBody = ({ children, className = '', compact = false, fullscreen = false }) => (
  <div
    className={[
      'ads-dialog__body',
      'modal-body',
      'mb-body',
      compact ? 'ads-dialog__body--compact mb-body--compact' : '',
      fullscreen ? 'ads-dialog__body--fullscreen mb-body--fullscreen' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ')}
  >
    {children}
  </div>
);

export const AdsDialogFooter = ({ children, className = '' }) => (
  <div className={['ads-dialog__footer', 'modal-footer', className].filter(Boolean).join(' ')}>
    {children}
  </div>
);

export default AdsDialog;
